<template>
  <div
    class="brief-content"
    :class="`brief-content--${variant}`"
  >
    <div
      class="brief-content__card"
      :class="{ 'brief-content__card--boxed': showMeta }"
    >
      <div class="brief-content__intro">
        <div class="brief-content__title">
          {{ brief.title || 'Untitled brief' }}
        </div>
        <div class="brief-content__owner">
          {{ ownerLabel }}
        </div>
      </div>

      <div
        v-if="showMeta"
        class="brief-content__meta"
      >
        <div>
          <div class="brief-content__meta-label">
            Assignee
          </div>
          <div class="brief-content__meta-value">
            {{ ownerLabel }}
          </div>
        </div>
        <div>
          <div class="brief-content__meta-label">
            Due
          </div>
          <div class="brief-content__meta-value">
            {{ formatDate(brief.delivery_date) }}
          </div>
        </div>
      </div>
    </div>

    <div class="brief-content__sections">
      <div
        v-if="brief.summary"
        class="brief-content__section"
      >
        <div class="brief-content__label">
          Summary
        </div>
        <div class="brief-content__body">
          {{ brief.summary }}
        </div>
      </div>

      <div
        v-for="section in proseSections"
        :key="sectionKey(section)"
        class="brief-content__section"
      >
        <div class="brief-content__label">
          {{ sectionLabel(section) }}
        </div>
        <RichTextDisplay
          class="brief-content__body"
          :html="section.content"
          show-image-actions
        />
      </div>

      <!-- The brief's requested images share one heading: their own labels are
           image-type names, which read as a list rather than as sections. -->
      <div
        v-if="imageSections.length"
        class="brief-content__section"
      >
        <div class="brief-content__label">
          Images requested
        </div>
        <div class="brief-content__images">
          <div
            v-for="section in imageSections"
            :key="sectionKey(section)"
            class="brief-content__image"
          >
            <!-- No creative direction to reveal → a plain row, not a dead toggle. -->
            <div
              v-if="!hasDirection(section)"
              class="brief-content__image-toggle brief-content__image-toggle--static"
            >
              <span class="brief-content__image-name">{{ sectionLabel(section) }}</span>
            </div>
            <template v-else>
              <button
                type="button"
                class="brief-content__image-toggle"
                :aria-expanded="isImageOpen(section) ? 'true' : 'false'"
                @click="toggleImageSection(section)"
              >
                <span class="brief-content__image-name">{{ sectionLabel(section) }}</span>
                <BriefDetailIcon
                  :name="isImageOpen(section) ? 'chevron-up' : 'chevron-down'"
                  size="sm"
                  class="brief-content__image-chevron"
                />
              </button>
              <RichTextDisplay
                v-if="isImageOpen(section)"
                class="brief-content__body brief-content__image-body"
                :html="section.content"
                show-image-actions
              />
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="references.length"
        class="brief-content__section"
      >
        <div class="brief-content__label">
          References
        </div>
        <div class="brief-content__refs">
          <div
            v-for="ref in references"
            :key="ref.id"
            class="brief-content__ref"
            :class="{ 'brief-content__ref--file': showsAsFile(ref) }"
          >
            <img
              v-if="showsAsImage(ref)"
              :src="referenceImgSrc(ref)"
              :alt="ref.name || ''"
              class="brief-content__ref-img"
              @error="onReferenceImgError(ref)"
            >
            <video
              v-else-if="showsAsVideo(ref)"
              :src="ref.url"
              class="brief-content__ref-img"
              muted
              playsinline
              @error="markReferenceUnrenderable(ref)"
            />
            <a
              v-else
              :href="ref.url"
              class="brief-content__ref-file"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="`Open ${ref.name || 'file'}`"
            >
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span
                class="brief-content__ref-file-icon"
                v-html="referenceFileIconMarkup(ref)"
              />
              <span class="brief-content__ref-file-name">{{ ref.name }}</span>
            </a>
            <div class="brief-content__ref-actions">
              <button
                v-if="!showsAsFile(ref)"
                type="button"
                class="brief-content__ref-action"
                aria-label="View larger"
                title="View larger"
                @click.stop="previewReference(ref)"
              >
                <BriefDetailIcon
                  name="search"
                  size="sm"
                />
              </button>
              <button
                type="button"
                class="brief-content__ref-action"
                :aria-label="`Download ${ref.name || 'file'}`"
                title="Download"
                @click.stop="downloadReference(ref)"
              >
                <BriefDetailIcon
                  name="download"
                  size="sm"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PreviewFileModal ref="preview" />
  </div>
</template>

<script>
/**
 * Read-only rendering of a linked brief: summary, prose sections, requested
 * images and reference thumbnails.
 *
 * Shared by every surface that shows a brief alongside creative work — the
 * layout sidebar's Brief tab, the shared proof page, and the Creative Studio
 * drawer — so formatting support (rich text, references, image sections) only
 * has to be built once. Hosts own their own chrome (header, tabs, footer
 * links) and pick a `variant` for the type scale. (POR-13996)
 */
import RichTextDisplay from '@frontend/components/common/editors/RichTextDisplay.vue';
import PreviewFileModal from '@frontend/components/common/PreviewFileModal.vue';
import BriefDetailIcon from '@frontend/components/briefs/BriefDetailIcon.vue';
import {
  briefSectionLabel,
  briefSectionTypeId,
  partitionBriefSections,
} from '@frontend/components/briefs/briefPanelSections';
import {
  downloadUrlAsFile,
  fullFileNameFromUrl,
  isImageReference,
  isVideoReference,
  referenceFileIconMarkup,
  referenceThumbnailUrl,
  referencesFromBrief,
} from '@frontend/components/briefs/briefReferenceUtils';
import { htmlIsEmpty } from '@frontend/utils/sanitizeHtml';

export default {
  name: 'BriefContentPanel',
  components: {
    RichTextDisplay,
    PreviewFileModal,
    BriefDetailIcon,
  },
  props: {
    brief: {
      type: Object,
      required: true,
    },
    // Type scale only — the markup is identical across hosts.
    variant: {
      type: String,
      default: 'proof',
      validator: (value) => ['sidebar', 'proof', 'drawer'].includes(value),
    },
    // Adds the boxed Assignee / Due row (the layout sidebar's linked-brief card).
    showMeta: {
      type: Boolean,
      default: false,
    },
    // 0 = render every section. The proof page caps at 8.
    maxSections: {
      type: Number,
      default: 0,
    },
    maxReferences: {
      type: Number,
      default: 9,
    },
    // 'viewer' resolves the label against the snapshot's is_owner flag ("You");
    // 'creator' always names the brief's creator, which is what an anonymous
    // proof-page visitor must see.
    ownerSource: {
      type: String,
      default: 'creator',
      validator: (value) => ['creator', 'viewer'].includes(value),
    },
  },
  data() {
    return {
      // type_id → collapsed. Image sections start expanded; only the ones the
      // user closes are tracked.
      collapsedImageSections: {},
      // id → true when an <img>/<video> failed so we swap to a file icon
      // instead of the browser's broken-image glyph.
      unrenderableReferenceIds: {},
      // id → true when the scaled thumbnail 404'd; retry the original URL.
      fullSizeReferenceIds: {},
    };
  },
  computed: {
    partitionedSections() {
      return partitionBriefSections(this.brief, { limit: this.maxSections });
    },
    proseSections() {
      return this.partitionedSections.prose;
    },
    imageSections() {
      return this.partitionedSections.images;
    },
    references() {
      return referencesFromBrief(this.brief)
        .filter((ref) => ref && ref.url)
        .slice(0, this.maxReferences);
    },
    ownerLabel() {
      const brief = this.brief || {};
      if (this.ownerSource === 'viewer' && brief.is_owner !== false) {
        return 'You';
      }
      return brief.creator_name
        || brief.submitter_display_name
        || brief.submitter_name
        || 'You';
    },
  },
  methods: {
    sectionKey(section) {
      return briefSectionTypeId(section);
    },
    sectionLabel(section) {
      return briefSectionLabel(section, this.brief ? this.brief.payload : null);
    },
    hasDirection(section) {
      return !htmlIsEmpty(section && section.content);
    },
    isImageOpen(section) {
      return !this.collapsedImageSections[briefSectionTypeId(section)];
    },
    toggleImageSection(section) {
      const typeId = briefSectionTypeId(section);
      this.$set(this.collapsedImageSections, typeId, !this.collapsedImageSections[typeId]);
    },
    formatDate(value) {
      if (!value) return '—';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return value;
      return parsed.toLocaleDateString();
    },
    showsAsImage(ref) {
      return isImageReference(ref) && !this.unrenderableReferenceIds[ref.id];
    },
    showsAsVideo(ref) {
      return isVideoReference(ref) && !this.unrenderableReferenceIds[ref.id];
    },
    showsAsFile(ref) {
      return !this.showsAsImage(ref) && !this.showsAsVideo(ref);
    },
    referenceFileIconMarkup,
    referenceImgSrc(ref) {
      if (ref?.id && this.fullSizeReferenceIds[ref.id]) {
        return ref.url;
      }
      return referenceThumbnailUrl(ref);
    },
    onReferenceImgError(ref) {
      if (!ref?.id) return;
      if (!this.fullSizeReferenceIds[ref.id] && this.referenceImgSrc(ref) !== ref.url) {
        this.$set(this.fullSizeReferenceIds, ref.id, true);
        return;
      }
      this.markReferenceUnrenderable(ref);
    },
    markReferenceUnrenderable(ref) {
      if (!ref?.id) return;
      this.$set(this.unrenderableReferenceIds, ref.id, true);
    },
    previewReference(ref) {
      if (!ref?.url) return;
      if (this.showsAsVideo(ref) && this.$refs.preview) {
        this.$refs.preview.show(ref.url, 'mp4');
        return;
      }
      // Lightbox always uses the original, not the scaled tile source.
      if (this.showsAsImage(ref) && this.$refs.preview) {
        this.$refs.preview.show(ref.url, 'image');
        return;
      }
      window.open(ref.url, '_blank', 'noopener,noreferrer');
    },
    async downloadReference(ref) {
      if (!ref?.url) return;
      const filename = (ref.name || '').trim()
        || fullFileNameFromUrl(ref.url)
        || 'download';
      await downloadUrlAsFile(ref.url, filename);
    },
  },
};
</script>

<style scoped lang="scss">
.brief-content {
  display: flex;
  flex-direction: column;
  color: #0f172a;
  font-size: 0.875rem;
}

.brief-content *,
.brief-content *::before,
.brief-content *::after {
  box-sizing: border-box;
}

.brief-content__card--boxed {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
}

.brief-content__intro {
  min-width: 0;
}

.brief-content__title {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25;
  word-break: break-word;
}

.brief-content__owner {
  margin-top: 0.25rem;
  font-size: 11px;
  color: #64748b;
}

.brief-content__meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding-top: 4px;
}

.brief-content__meta-label {
  color: #64748b;
  font-size: 11px;
  line-height: 16px;
}

.brief-content__meta-value {
  margin-top: 2px;
  color: #0f172a;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.brief-content__sections {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-top: 1.25rem;
}

.brief-content__section {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.brief-content__label {
  margin-bottom: 0.375rem;
  color: #64748b;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.brief-content__body {
  font-size: 13px;
  line-height: 1.55;
  color: #0f172a;
  word-break: break-word;
  white-space: pre-wrap;
}

.brief-content__body ::v-deep .rich-text-display {
  white-space: normal;
}

.brief-content__body ::v-deep .rich-text-display p {
  margin: 0 0 0.5rem;
}

.brief-content__body ::v-deep .rich-text-display p:last-child {
  margin-bottom: 0;
}

.brief-content__body ::v-deep .rich-text-display ol,
.brief-content__body ::v-deep .rich-text-display ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
  list-style-position: outside;
}

.brief-content__body ::v-deep .rich-text-display ol {
  list-style-type: decimal;
}

.brief-content__body ::v-deep .rich-text-display ul {
  list-style-type: disc;
}

.brief-content__body ::v-deep .rich-text-display li {
  display: list-item;
  margin: 0.25rem 0;
}

.brief-content__images {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.brief-content__image {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}

.brief-content__image-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  color: #0f172a;
  font-size: 13px;
  line-height: 1.35;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: rgba(241, 245, 249, 0.6);
  }
}

.brief-content__image-toggle--static {
  cursor: default;

  &:hover {
    background: transparent;
  }
}

.brief-content__image-name {
  min-width: 0;
  word-break: break-word;
}

.brief-content__image-chevron {
  flex-shrink: 0;
  color: #64748b;
}

.brief-content__image-body {
  padding: 0 10px 10px;
}

.brief-content__refs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.brief-content__ref {
  position: relative;
  aspect-ratio: 1 / 1;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
  background: #f1f5f9;
}

.brief-content__ref:hover .brief-content__ref-actions {
  opacity: 1;
}

.brief-content__ref--file {
  background: #fff;
}

.brief-content__ref-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brief-content__ref-file {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  height: 100%;
  padding: 0.5rem 0.35rem 0.35rem;
  text-decoration: none;
  color: inherit;
}

.brief-content__ref-file-icon {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;
  max-height: 62%;
}

.brief-content__ref-file-icon ::v-deep svg,
.brief-content__ref-file-icon ::v-deep img {
  max-width: 100%;
  max-height: 2.5rem;
  width: auto;
  height: auto;
}

.brief-content__ref-file-name {
  font-size: 0.5625rem;
  line-height: 1.25;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  color: #334155;
}

.brief-content__ref-actions {
  position: absolute;
  top: 0.35rem;
  right: 0.35rem;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.brief-content__ref-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: hsl(0 0% 100% / 0.92);
  color: hsl(222.2, 47.4%, 11.2%);
  box-shadow: 0 1px 2px hsl(0 0% 0% / 0.08);
  cursor: pointer;
  line-height: 1;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: hsl(0 0% 100%);
    color: #189a73;
  }
}

/* Layout sidebar: `.b-sidebar * { font-size: .875rem }` from LayoutSidebar.vue
   forces every size here, hence !important on the whole scale. */
.brief-content--sidebar {
  font-size: 14px !important;
  line-height: 1.5 !important;
  -webkit-font-smoothing: antialiased;

  .brief-content__title {
    font-size: 14px !important;
    font-weight: 600 !important;
    line-height: 20px !important;
  }

  .brief-content__owner {
    margin-top: 2px;
    font-size: 12px !important;
    font-weight: 400 !important;
    line-height: 16px !important;
  }

  .brief-content__meta-label {
    font-size: 11px !important;
    font-weight: 400 !important;
    line-height: 16px !important;
    text-transform: none !important;
    letter-spacing: normal !important;
  }

  .brief-content__meta-value {
    font-size: 12px !important;
    font-weight: 600 !important;
    line-height: 16px !important;
  }

  .brief-content__sections {
    gap: 16px;
    margin-top: 16px;
  }

  .brief-content__label {
    margin: 0 0 8px;
    padding: 0;
    font-size: 10px !important;
    font-weight: 600 !important;
    letter-spacing: 0.05em !important;
    line-height: 14px !important;
    text-transform: uppercase !important;
  }

  .brief-content__body {
    margin: 0;
    padding: 0;
    font-size: 14px !important;
    font-weight: 400 !important;
    line-height: 1.625 !important;
    text-indent: 0 !important;
  }

  .brief-content__image-body {
    padding: 0 10px 10px;
  }

  .brief-content__image-toggle {
    font-size: 14px !important;
    line-height: 20px !important;
  }

  .brief-content__body ::v-deep .rich-text-display,
  .brief-content__body ::v-deep .rich-text-display * {
    font-size: 14px !important;
    line-height: 1.625 !important;
    white-space: normal;
  }

  .brief-content__body ::v-deep .rich-text-display p {
    margin: 0 0 0.5rem !important;
  }

  .brief-content__body ::v-deep .rich-text-display p:last-child {
    margin-bottom: 0 !important;
  }

  .brief-content__body ::v-deep .rich-text-display ol,
  .brief-content__body ::v-deep .rich-text-display ul {
    margin: 0.5rem 0 !important;
    padding-left: 1.5rem !important;
  }

  .brief-content__body ::v-deep .rich-text-display li {
    display: list-item !important;
    margin: 0.25rem 0 !important;
  }
}

/* Creative Studio drawer: roomier than the two in-page panels. */
.brief-content--drawer {
  .brief-content__title {
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
  }

  .brief-content__owner {
    display: none;
  }

  .brief-content__sections {
    margin-top: 1rem;
    gap: 1.5rem;
  }

  .brief-content__body {
    font-size: 14px;
    line-height: 1.6;
  }
}
</style>
